import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import { Box, SvgIconProps, Typography } from "@mui/material";
import { formatDisplayPhoneNumber, useTranslation } from "hhn-commons";
import React from "react";
import { DrawerDivider } from "../../../../layout/RightDrawer.style";
import { sortBy } from "lodash";
import { IGroupInfo } from "@home-health-notify/domain";

function ServiceInfoItem({
  icon,
  title,
  value,
}: {
  title: string;
  value: string;
  icon: React.ReactNode;
}) {
  const clonedIcon = React.isValidElement(icon)
    ? React.cloneElement(icon as React.ReactElement<SvgIconProps>, {
        color: "secondary",
      })
    : icon;
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        marginBottom: "20px",
        gap: "10px",
      }}
    >
      {clonedIcon}
      <Box>
        <Typography>{title}</Typography>
        <Typography>{value}</Typography>
      </Box>
    </Box>
  );
}

export const ServiceInfo = ({
  groupInfo,
  canViewPatientInfo = false,
}: {
  groupInfo: IGroupInfo;
  canViewPatientInfo?: boolean;
}) => {
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "groupChatInfo" });
  const sortedPhysicians = sortBy(groupInfo.info?.physician, ["sortOrder"]);
  const sortedCaregivers = canViewPatientInfo
    ? sortBy(groupInfo.info?.careGiver, ["sortOrder"])
    : [];
  const sortedPharmacies = canViewPatientInfo
    ? sortBy(groupInfo.info?.pharmacy, ["sortOrder"])
    : [];

  return (
    <Box>
      <Box sx={{ padding: "20px" }}>
        <Typography sx={{ marginBottom: "20px" }}>{tNs("info")}</Typography>
        {sortedPhysicians.map(physician => (
          <Box key={physician.name} data-testid="physician-info">
            <Typography sx={{ marginBottom: "20px" }}>
              {tNs("physicianDetails")}
            </Typography>
            <ServiceInfoItem
              icon={<PersonIcon />}
              title={tNs("physicianName")}
              value={physician.name}
            />
            <ServiceInfoItem
              icon={<PhoneIcon />}
              title={tNs("physicianNumber")}
              value={formatDisplayPhoneNumber(
                physician.countryCode,
                physician.phoneNo,
              )}
            />
          </Box>
        ))}
        {sortedCaregivers.map(caregiver => (
          <Box key={caregiver.name} data-testid="caregiver-info">
            <Typography sx={{ marginBottom: "20px" }}>
              {tNs("careGiverDetails")}
            </Typography>
            <ServiceInfoItem
              icon={<PersonIcon />}
              title={tNs("careGiverName")}
              value={caregiver.name}
            />
            <ServiceInfoItem
              icon={<PhoneIcon />}
              title={tNs("careGiverNumber")}
              value={formatDisplayPhoneNumber(
                caregiver.countryCode,
                caregiver.phoneNo,
              )}
            />
          </Box>
        ))}
        {sortedPharmacies.map(pharmacy => (
          <Box key={pharmacy.name} data-testid="pharmacy-info">
            <Typography sx={{ marginBottom: "20px" }}>
              {tNs("pharmacyDetails")}
            </Typography>
            <ServiceInfoItem
              icon={<PersonIcon />}
              title={tNs("pharmacyName")}
              value={pharmacy.name}
            />
            <ServiceInfoItem
              icon={<PhoneIcon />}
              title={tNs("pharmacyNumber")}
              value={formatDisplayPhoneNumber(
                pharmacy.countryCode,
                pharmacy.phoneNo,
              )}
            />
          </Box>
        ))}
      </Box>
      <DrawerDivider />
    </Box>
  );
};
